import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { verifyMagicLink } from "../api/client";
import { BRAND } from "../brand";

export default function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    if (!token) {
      setError("Invalid magic link.");
      return;
    }

    void (async () => {
      try {
        await verifyMagicLink(token);
        navigate("/activities", { replace: true });
      } catch {
        setError("This login link is invalid or has expired.");
      }
    })();
  }, [navigate, token]);

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4" data-testid="auth-callback-error-state">
        <div className="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm dark:border-[#2e2e2e] dark:bg-[#1a1a1a]">
          <div className="mb-4 flex items-center justify-center gap-2.5">
            <img src={BRAND.logoPath} alt={BRAND.name} className="h-9 w-9" />
            <span className="text-xl font-semibold tracking-tight">{BRAND.name}</span>
          </div>
          <h1 className="text-lg font-semibold" data-testid="auth-callback-error-title">Sign in failed</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{error}</p>
          <Link to="/login" className="mt-5 inline-block rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700" data-testid="auth-callback-retry-link">Back to Sign In</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4" data-testid="auth-callback-loading-state">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-emerald-600 dark:border-[#2e2e2e] dark:border-t-emerald-400" role="status" aria-label="Signing you in" />
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Signing you in…</p>
      </div>
    </main>
  );
}
